import { useTranslation } from 'react-i18next';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { TrendingUp, UtensilsCrossed, Bookmark, Star } from "lucide-react";
import { ResponsiveContainer } from 'recharts/lib/component/ResponsiveContainer';
import { AreaChart } from 'recharts/lib/chart/AreaChart';
import { Area } from 'recharts/lib/cartesian/Area';
import { XAxis } from 'recharts/lib/cartesian/XAxis';
import { YAxis } from 'recharts/lib/cartesian/YAxis';
import { CartesianGrid } from 'recharts/lib/cartesian/CartesianGrid';
import { Tooltip } from 'recharts/lib/component/Tooltip';
import { Legend } from 'recharts/lib/component/Legend';

interface ChartData {
  name: string;
  beef: number;
  chicken: number;
  fish: number;
}

// Sample data for the chart
const data: ChartData[] = [
  { name: 'Jan', beef: 32, chicken: 22, fish: 18 },
  { name: 'Feb', beef: 38, chicken: 24, fish: 19 },
  { name: 'Mar', beef: 34, chicken: 26, fish: 22 },
  { name: 'Apr', beef: 35, chicken: 23, fish: 20 },
  { name: 'May', beef: 40, chicken: 28, fish: 21 },
  { name: 'Jun', beef: 42, chicken: 30, fish: 24 },
  { name: 'Jul', beef: 44, chicken: 32, fish: 25 },
  { name: 'Aug', beef: 45, chicken: 34, fish: 24 },
  { name: 'Sep', beef: 48, chicken: 36, fish: 27 },
  { name: 'Oct', beef: 50, chicken: 35, fish: 25 },
  { name: 'Nov', beef: 52, chicken: 38, fish: 29 },
  { name: 'Dec', beef: 56, chicken: 40, fish: 30 },
];

const Dashboard = () => {
  const { t } = useTranslation();

  const stats = [
    {
      id: "total-recipes",
      title: t('dashboard.stats.totalRecipes.title'),
      value: "248",
      icon: <UtensilsCrossed className="h-6 w-6 text-primary" />,
      description: t('dashboard.stats.totalRecipes.description'),
    },
    {
      id: "favorites",
      title: t('dashboard.stats.favorites.title'),
      value: "42",
      icon: <Bookmark className="h-6 w-6 text-indigo-500" />,
      description: t('dashboard.stats.favorites.description'),
    },
    {
      id: "popular",
      title: t('dashboard.stats.popular.title'),
      value: "Ropa Vieja Canaria",
      icon: <TrendingUp className="h-6 w-6 text-green-500" />,
      description: t('dashboard.stats.popular.description'),
    },
    {
      id: "highest-rated",
      title: t('dashboard.stats.highestRated.title'),
      value: "4.9/5",
      icon: <Star className="h-6 w-6 text-amber-500" />,
      description: t('dashboard.stats.highestRated.description'),
    },
  ];

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-semibold tracking-tight">{t('dashboard.title')}</h1>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <Card key={stat.id} className="hover-scale">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {stat.title}
                  </p>
                  <p className="text-3xl font-bold">{stat.value}</p>
                </div>
                <div className="p-2 rounded-full bg-primary/10">{stat.icon}</div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">{stat.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recipe Management Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="hover-scale">
          <CardHeader>
            <CardTitle>{t('dashboard.recentRecipes.title')}</CardTitle>
            <CardDescription>{t('dashboard.recentRecipes.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-muted-foreground">
              {t('dashboard.recentRecipes.empty')}
            </div>
          </CardContent>
        </Card>
        <Card className="hover-scale">
          <CardHeader>
            <CardTitle>{t('dashboard.cookingTips.title')}</CardTitle>
            <CardDescription>{t('dashboard.cookingTips.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-muted-foreground">
              {t('dashboard.cookingTips.empty')}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart Section */}
      <Card className="hover-scale">
        <CardHeader>
          <CardTitle>{t('dashboard.ingredientCosts.title')}</CardTitle>
          <CardDescription>{t('dashboard.ingredientCosts.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-96 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={data}
                margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
              >
                <defs>
                  <linearGradient id="beef" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="chicken" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="fish" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    border: 'none',
                  }}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="beef"
                  stroke="#ef4444"
                  fillOpacity={1}
                  fill="url(#beef)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="chicken"
                  stroke="#3b82f6"
                  fillOpacity={1}
                  fill="url(#chicken)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="fish"
                  stroke="#10b981"
                  fillOpacity={1}
                  fill="url(#fish)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
